(function () {
  if (!window.HbElements || !window.subscribe || !window.publish) return;

  var REGION_SELECTOR = '[data-hb-cart-region]';
  var BUBBLE_SECTION = 'cart-icon-bubble';
  var SUBSCRIBE_ALL_LIMIT = 50;

  function allRegions() {
    return Array.prototype.slice.call(document.querySelectorAll(REGION_SELECTOR));
  }

  /**
   * Nested cart blocks (items, upsell, summary) live inside filled/empty wrappers.
   * Swapping a parent first detaches the children; only swap roots so children ride along.
   */
  function regions() {
    return allRegions().filter(function (el) {
      var parent = el.parentElement;
      return !(parent && parent.closest(REGION_SELECTOR));
    });
  }

  /**
   * Cart JSON splits empty promo, shipping, filled body, and empty recs across separate
   * custom-block sections. Asking for only the first region's section id (the empty promo)
   * returns HTML that cannot replace items or totals, so the page looks unchanged until refresh.
   * The Section Rendering API accepts at most five ids per request.
   */
  var SECTIONS_REQUEST_LIMIT = 5;

  function regionSectionIds() {
    var ids = [];
    regions().forEach(function (el) {
      var id = el.dataset.sectionId;
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function sectionsRequestIds() {
    var ids = regionSectionIds().slice();
    if (ids.indexOf(BUBBLE_SECTION) === -1 && ids.length < SECTIONS_REQUEST_LIMIT) {
      ids.push(BUBBLE_SECTION);
    }
    return ids.slice(0, SECTIONS_REQUEST_LIMIT);
  }

  function combinedSectionHtml(sections) {
    if (!sections) return '';
    return Object.keys(sections)
      .filter(function (key) {
        return key !== BUBBLE_SECTION && sections[key];
      })
      .map(function (key) {
        return sections[key];
      })
      .join('');
  }

  function parsedSectionDoc(sections) {
    var html = combinedSectionHtml(sections);
    return html ? new DOMParser().parseFromString(html, 'text/html') : null;
  }

  function docCoversRegions(doc) {
    if (!doc) return false;
    return regions().every(function (el) {
      var id = el.dataset.blockId;
      if (id) return !!doc.querySelector('[data-block-id="' + escapeSelector(id) + '"]');
      return !!doc.querySelector(el.tagName.toLowerCase());
    });
  }

  function escapeSelector(value) {
    if (window.CSS && typeof CSS.escape === 'function') return CSS.escape(value);
    return String(value).replace(/["\\]/g, '\\$&');
  }

  function fill(template, token, value) {
    if (!template) return '';
    return template.replace(token, value);
  }

  function standardEventTarget(kind) {
    if (kind === 'discount') {
      return document.querySelector('hb-cart-discount') || document;
    }
    return document.querySelector('hb-cart-items') || document;
  }

  function startCartStandardEvent(payload, options) {
    var StandardEvents = window.StandardEvents || {};
    var target;
    var deferred;

    if (Object.prototype.hasOwnProperty.call(payload, 'discount')) {
      var DiscountEvent = StandardEvents.CartDiscountUpdateEvent;
      if (!DiscountEvent) return null;
      target = standardEventTarget('discount');
      deferred = DiscountEvent.createPromise();
      var codes = options.discountCodes || [];
      target.dispatchEvent(
        new DiscountEvent({
          discountCodes: codes.map(function (code) {
            return { code: String(code) };
          }),
          promise: deferred.promise,
        })
      );
      return { deferred: deferred, EventClass: DiscountEvent, target: target, settled: false };
    }

    var LinesEvent = StandardEvents.CartLinesUpdateEvent;
    if (!LinesEvent) return null;
    var lineKey = options.lineKey || payload.id || '';
    if (!lineKey) return null;
    var quantity = payload.quantity;
    var action = quantity === 0 ? 'remove' : 'update';
    target = standardEventTarget('lines');
    deferred = LinesEvent.createPromise();
    target.dispatchEvent(
      new LinesEvent({
        action: action,
        context: 'cart',
        lines: [{ id: lineKey, quantity: quantity }],
        promise: deferred.promise,
      })
    );
    return { deferred: deferred, EventClass: LinesEvent, target: target, settled: false };
  }

  function failCartStandardEvent(handle, message, code) {
    if (!handle || handle.settled) return;
    handle.settled = true;
    var CartErrorEvent = window.StandardEvents && window.StandardEvents.CartErrorEvent;
    if (CartErrorEvent && handle.target) {
      handle.target.dispatchEvent(
        new CartErrorEvent({ error: message || '', code: code || 'INVALID' })
      );
    }
    handle.deferred.reject(new Error(message || 'Cart request failed'));
  }

  function succeedCartStandardEvent(handle, cartData) {
    if (!handle || handle.settled) return;
    handle.settled = true;
    if (!cartData || !cartData.currency) {
      handle.deferred.reject(new Error('Missing currency in cart response'));
      return;
    }
    handle.deferred.resolve({ cart: handle.EventClass.createCartFromAjaxResponse(cartData) });
  }

  /**
   * Serialises every cart write. A selling-plan change rewrites the line key and can merge the
   * line with an existing identical one, which renumbers every later line index — so overlapping
   * requests would act on stale indices.
   */
  var CartController = {
    queue: Promise.resolve(),

    enqueue: function (task) {
      var run = this.queue.then(task, task);
      this.queue = run.catch(function () {});
      return run;
    },

    setLoading: function (isLoading) {
      allRegions().forEach(function (el) {
        el.classList.toggle('is-loading', isLoading);
        el.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      });
    },

    request: function (payload, url) {
      var body = Object.assign({}, payload);
      body.sections = sectionsRequestIds();
      body.sections_url = window.location.pathname;

      var config = fetchConfig('javascript');
      config.body = JSON.stringify(body);

      return fetch(url || routes.cart_change_url, config).then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, data: data };
        });
      });
    },

    mutate: function (payload, context) {
      var self = this;
      var options = context || {};

      return this.enqueue(function () {
        self.setLoading(true);
        var standardEvent = startCartStandardEvent(payload, options);

        return self
          .request(payload, options.url)
          .then(function (result) {
            if (!result.ok || result.data.status) {
              var message =
                (result.data && (result.data.description || result.data.message)) ||
                (window.cartStrings && window.cartStrings.error) ||
                '';
              failCartStandardEvent(standardEvent, message, 'INVALID');
              return self.recoverFromError(result.data, options);
            }
            // render() resolves asynchronously on its fallback path, so keep it inside the queued
            // task - otherwise a later mutation runs against un-swapped DOM.
            return Promise.resolve(self.render(result.data)).then(function () {
              succeedCartStandardEvent(standardEvent, result.data);
              self.announce(options.announcement);
              // render() replaced the region, so a client-side rule notice has to be re-applied.
              if (options.notice) self.showLineNotice(options.lineKey, options.notice);
              return result.data;
            });
          })
          .catch(function (error) {
            failCartStandardEvent(
              standardEvent,
              (window.cartStrings && window.cartStrings.error) || (error && error.message) || '',
              'SERVICE_UNAVAILABLE'
            );
            return self.recoverFromError(null, options);
          })
          .then(function (data) {
            self.setLoading(false);
            return data;
          });
      });
    },

    /**
     * A 422 does not mean the cart is untouched: Shopify clamps an over-large quantity to available
     * stock, mutates the cart, and returns 422 with no `sections`. So always re-read the real state
     * before showing the message, and only fall back to reverting the input if that read fails too.
     */
    recoverFromError: function (data, options) {
      var self = this;

      return this.fetchAndSwap()
        .catch(function () {
          if (typeof options.revert === 'function') options.revert();
        })
        .then(function () {
          self.showError(data, options);
          return null;
        });
    },

    /**
     * Raw fetch + swap. Must not enqueue — it is called from inside queued tasks.
     * Refreshes the header bubble too: this path is reached when the response carried no
     * `sections` (a 422) or when another component mutated the cart.
     */
    fetchAndSwap: function () {
      var self = this;
      var ids = regionSectionIds().slice(0, SECTIONS_REQUEST_LIMIT);
      var path = window.location.pathname;

      var fromPage = function () {
        return fetch(path)
          .then(function (response) {
            return response.ok ? response.text() : '';
          })
          .then(function (text) {
            if (!text) return;
            var doc = new DOMParser().parseFromString(text, 'text/html');
            self.swapRegions(doc);
            var next = doc.querySelector('#cart-icon-bubble');
            var target = document.getElementById('cart-icon-bubble');
            if (next && target) target.innerHTML = next.innerHTML;
          })
          .catch(function () {});
      };

      if (!ids.length) return fromPage();

      return fetch(path + '?sections=' + ids.map(encodeURIComponent).join(','))
        .then(function (response) {
          return response.ok ? response.json() : null;
        })
        .then(function (sections) {
          var doc = parsedSectionDoc(sections);
          if (!docCoversRegions(doc)) return fromPage();
          self.swapRegions(doc);

          return fetch(path + '?section_id=' + BUBBLE_SECTION)
            .then(function (response) {
              return response.ok ? response.text() : null;
            })
            .then(function (bubbleHtml) {
              if (bubbleHtml) self.renderBubble(bubbleHtml);
            })
            .catch(function () {});
        })
        .catch(function () {
          return fromPage();
        });
    },

    /** Reverse sync: something else (drawer, add-all, an app) changed the cart. */
    refreshFromServer: function () {
      var self = this;

      return this.enqueue(function () {
        return self.fetchAndSwap().catch(function () {});
      });
    },

    /**
     * Prefers an exact tag + data-block-id match, then falls back to the same-index element of the
     * same tag: the block-id prefix is derived from the theme and can change between renders.
     * Returns how many regions were actually replaced so callers can detect a silent no-op.
     */
    swapRegions: function (doc) {
      var swapped = 0;

      regions().forEach(function (el) {
        var tag = el.tagName.toLowerCase();
        var next = doc.querySelector(tag + '[data-block-id="' + escapeSelector(el.dataset.blockId) + '"]');

        if (!next) {
          var index = Array.prototype.indexOf.call(document.querySelectorAll(tag), el);
          var candidates = doc.querySelectorAll(tag);
          next = index >= 0 ? candidates[index] : candidates[0];
        }
        if (!next) return;

        el.replaceWith(document.importNode(next, true));
        swapped += 1;
      });

      return swapped;
    },

    render: function (cartData) {
      var self = this;
      var sections = cartData.sections || {};
      var focus = this.captureFocus();
      var doc = parsedSectionDoc(sections);
      var swapped = docCoversRegions(doc) ? this.swapRegions(doc) : 0;

      this.renderBubble(sections[BUBBLE_SECTION]);

      var drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.classList.toggle('is-empty', cartData.item_count === 0);

      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'hb-cart', cartData: cartData });

      if (!swapped) {
        // Section Rendering can legitimately return nothing — e.g. a dev session serving an unsaved
        // template, where the section id carries a uuid the section route cannot resolve. Without
        // this the page would silently keep showing stale quantities and totals.
        return this.fetchAndSwap().then(function () {
          self.restoreFocus(focus);
        });
      }

      this.restoreFocus(focus);
      return Promise.resolve();
    },

    renderBubble: function (html) {
      if (!html) return;
      var target = document.getElementById('cart-icon-bubble');
      if (!target) return;
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var source = parsed.querySelector('.shopify-section') || parsed.body;
      target.innerHTML = source.innerHTML;
    },

    /** replaceWith destroys the focused node, so remember which control on which line had focus. */
    captureFocus: function () {
      var active = document.activeElement;
      if (!active || typeof active.closest !== 'function') return null;
      var line = active.closest('[data-line-key]');
      if (!line) return null;
      return { key: line.dataset.lineKey, control: active.dataset.cartControl || null };
    },

    restoreFocus: function (focus) {
      if (!focus) return;
      var line = document.querySelector('[data-line-key="' + escapeSelector(focus.key) + '"]');
      var target = null;

      var focusable = function (el) {
        return el && !el.disabled && el.getAttribute('aria-disabled') !== 'true';
      };

      if (line) {
        target = focus.control
          ? line.querySelector('[data-cart-control="' + focus.control + '"]')
          : null;
        // The plus button comes back disabled at the maximum; focusing it would drop to <body>.
        if (!focusable(target)) {
          target = Array.prototype.find.call(line.querySelectorAll('[data-cart-control]'), focusable);
        }
      } else {
        target = document.querySelector('hb-cart-items [data-cart-control]');
      }

      if (target) target.focus();
    },

    announce: function (message) {
      if (!message) return;
      var region = document.querySelector('[data-cart-live-region]');
      if (region) region.textContent = message;
    },

    showLineNotice: function (lineKey, message) {
      if (!lineKey || !message) return;
      var line = document.querySelector('[data-line-key="' + escapeSelector(lineKey) + '"]');
      var target = line ? line.querySelector('[data-line-error]') : null;
      if (!target) return;
      target.textContent = message;
      target.hidden = false;
    },

    /** Looked up by line key, not by a captured element — the swap above destroys the old node. */
    showError: function (data, options) {
      var message =
        (data && (data.description || data.message)) ||
        (window.cartStrings && window.cartStrings.error) ||
        '';
      var target = null;

      if (options.lineKey) {
        var line = document.querySelector('[data-line-key="' + escapeSelector(options.lineKey) + '"]');
        target = line ? line.querySelector('[data-line-error]') : null;
      }
      target = target || document.querySelector('[data-cart-error]');

      if (target) {
        target.textContent = message;
        target.hidden = false;
      }

      publish(PUB_SUB_EVENTS.cartError, {
        source: 'hb-cart',
        message: message,
        errors: data && data.errors,
      });
    },

    /**
     * Applies the subscription plan one line at a time, re-reading the plan map from the freshly
     * rendered upsell block after each write. Precomputing the whole index list would break as
     * soon as a plan change merges two lines.
     */
    subscribing: false,

    subscribeAll: function () {
      var self = this;
      // The clicked button is destroyed by the first swap and comes back enabled, so the re-render
      // would otherwise let a second chain start mid-operation.
      if (this.subscribing) return Promise.resolve();
      this.subscribing = true;
      var step = function (remaining) {
        if (remaining <= 0) return Promise.resolve();

        var host = document.querySelector('hb-cart-upsell');
        if (!host) return Promise.resolve();

        var plan;
        try {
          plan = JSON.parse(host.dataset.planMap || '[]');
        } catch (error) {
          return Promise.resolve();
        }
        if (!plan.length) return Promise.resolve();

        var next = plan[0];
        return self
          .mutate(
            { line: next.line, quantity: next.quantity, selling_plan: next.planId },
            { announcement: host.dataset.stringSubscribed }
          )
          .then(function (data) {
            if (!data) return null;
            return step(remaining - 1);
          });
      };

      var clear = function () {
        self.subscribing = false;
      };
      return step(SUBSCRIBE_ALL_LIMIT).then(clear, clear);
    },

    setDiscounts: function (codes) {
      return this.mutate(
        { discount: codes.length ? codes.join(',') : '' },
        { url: routes.cart_update_url, discountCodes: codes.slice() }
      );
    },
  };

  class HbCartItems extends HbElements.Component {
    onConnect() {
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('submit', this.onSubmit);
    }

    onDisconnect() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('submit', this.onSubmit);
    }

    /**
     * The line-item form exists so the cart still works without JS. With JS we keep the shopper on
     * the page and push the changed quantities through the same queued path as the steppers.
     */
    onSubmit(event) {
      event.preventDefault();

      Array.prototype.forEach.call(
        this.querySelectorAll('[data-cart-control="quantity"]'),
        (input) => {
          const previous = Number(input.getAttribute('value'));
          const current = Number(input.value);
          if (!Number.isFinite(current) || current === previous) return;
          const context = this.lineContext(input);
          if (!context) return;
          this.clearError(context);
          this.setQuantity(context, input, current);
        }
      );
    }

    lineContext(el) {
      const line = el.closest('[data-line-key]');
      if (!line) return null;
      return {
        line,
        key: line.dataset.lineKey,
        index: Number(line.dataset.line),
        quantity: Number(line.dataset.quantity) || 1,
        title: line.dataset.lineTitle || '',
        errorTarget: line.querySelector('[data-line-error]'),
      };
    }

    clearError(context) {
      if (!context.errorTarget) return;
      context.errorTarget.textContent = '';
      context.errorTarget.hidden = true;
    }

    onClick(event) {
      const control = event.target.closest('[data-cart-control]');
      if (!control || !this.contains(control)) return;

      const action = control.dataset.cartControl;
      if (action !== 'decrease' && action !== 'increase' && action !== 'remove') {
        return;
      }

      event.preventDefault();
      if (control.disabled || control.getAttribute('aria-disabled') === 'true') return;

      const context = this.lineContext(control);
      if (!context) return;
      this.clearError(context);

      if (action === 'remove') {
        this.removeLine(context);
        return;
      }

      const input = context.line.querySelector('[data-cart-control="quantity"]');
      if (!input) return;
      const step = Number(input.dataset.step) || 1;
      const current = Number(input.value) || 0;
      this.setQuantity(context, input, action === 'increase' ? current + step : current - step);
    }

    onChange(event) {
      const input = event.target.closest('[data-cart-control="quantity"]');
      if (!input || !this.contains(input)) return;
      const context = this.lineContext(input);
      if (!context) return;
      this.clearError(context);

      // A cleared or browser-rejected number field surfaces as '', and Number('') === 0 would be
      // read as "remove this line". Restore the last known quantity instead.
      if (input.value.trim() === '') {
        input.value = input.getAttribute('value') || String(context.quantity);
        return;
      }

      this.setQuantity(context, input, Number(input.value));
    }

    removeLine(context) {
      CartController.mutate(
        { id: context.key, quantity: 0 },
        {
          lineKey: context.key,
          announcement: fill(this.dataset.stringRemoved, '[title]', context.title),
        }
      );
    }

    ruleMessage(kind, value) {
      const strings = window.quickOrderListStrings || {};
      return fill(strings[kind + '_error'], '[' + kind + ']', value);
    }

    setQuantity(context, input, requested) {
      const min = Number(input.dataset.min) || 1;
      const rawMax = input.dataset.max;
      let max = rawMax === undefined || rawMax === '' ? null : Number(rawMax);
      // A sold-out tracked variant can report a max under the minimum; clamping to it would hit the
      // removal branch below and delete the line.
      if (max !== null && (!Number.isFinite(max) || max < min)) max = min;
      const step = Number(input.dataset.step) || 1;
      const previous = Number(input.getAttribute('value')) || min;

      let quantity = Number.isFinite(requested) ? requested : previous;
      let message = '';

      // Stepping below the rule minimum has only one legal destination: gone.
      if (quantity <= 0 || (quantity < min && quantity < previous)) {
        this.removeLine(context);
        return;
      }

      if (quantity < min) {
        quantity = min;
        message = this.ruleMessage('min', min);
      }

      if (max !== null && quantity > max) {
        quantity = max;
        message = this.ruleMessage('max', max);
      }

      if (step > 1) {
        const snapped = min + Math.round((quantity - min) / step) * step;
        if (snapped !== quantity) {
          quantity = snapped;
          message = this.ruleMessage('step', step);
        }
        if (max !== null && quantity > max) {
          quantity = min + Math.floor((max - min) / step) * step;
        }
      }

      if (quantity === previous) {
        input.value = previous;
        if (message) CartController.showLineNotice(context.key, message);
        return;
      }

      input.value = quantity;
      // Keep the attribute in step so the submit path does not see a second pending change and
      // issue a duplicate write when Enter fires change and submit together.
      input.setAttribute('value', String(quantity));

      CartController.mutate(
        { id: context.key, quantity },
        {
          lineKey: context.key,
          notice: message,
          revert: () => {
            input.value = previous;
          },
          announcement: fill(
            fill(this.dataset.stringUpdated, '[title]', context.title),
            '[quantity]',
            quantity
          ),
        }
      );
    }
  }

  class HbCartUpsell extends HbElements.Component {
    onConnect() {
      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);
      // Region swap replaces this node mid subscribe-all; restore the button loading state.
      if (CartController.subscribing) this.setButtonLoading(true);
    }

    onDisconnect() {
      this.removeEventListener('click', this.onClick);
    }

    setButtonLoading(loading) {
      const button = this.querySelector('[data-subscribe-all]');
      if (!button) return;
      const spinner = button.querySelector('.loading__spinner');
      const reduceMotion = HbElements.prefersReducedMotion();

      button.classList.toggle('loading', loading);
      button.disabled = loading;
      button.setAttribute('aria-busy', loading ? 'true' : 'false');

      // Prefer spinner; under reduced motion keep the label readable instead.
      if (spinner) {
        spinner.classList.toggle('hidden', !loading || reduceMotion);
      }
      if (reduceMotion) {
        button.classList.toggle('loading--label', loading);
      } else {
        button.classList.remove('loading--label');
      }
    }

    onClick(event) {
      const button = event.target.closest('[data-subscribe-all]');
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      if (button.disabled) return;

      this.setButtonLoading(true);

      const host = this;
      CartController.subscribeAll().then(function () {
        // The block re-renders itself out of existence once every line is subscribed.
        if (!host.isConnected) return;
        host.setButtonLoading(false);
      });
    }
  }

  HbElements.define('hb-cart-items', HbCartItems);
  HbElements.define('hb-cart-upsell', HbCartUpsell);

  class HbCartLinePlan extends HbElements.Component {
    onConnect() {
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
    }

    onDisconnect() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
    }

    lineContext() {
      return {
        index: this.numAttr('line', 0),
        quantity: this.numAttr('quantity', 1),
        key: this.dataset.lineKey,
        title: this.dataset.lineTitle || '',
      };
    }

    writeSellingPlan(planId, announcementTemplate) {
      const context = this.lineContext();
      if (!context.index) return;

      CartController.mutate(
        {
          line: context.index,
          quantity: context.quantity,
          selling_plan: planId,
        },
        {
          lineKey: context.key,
          announcement: fill(announcementTemplate, '[title]', context.title),
        }
      );
    }

    onClick(event) {
      const control = event.target.closest('[data-cart-control]');
      if (!control || !this.contains(control)) return;

      const action = control.dataset.cartControl;
      if (action !== 'subscribe' && action !== 'unsubscribe') return;

      event.preventDefault();
      event.stopPropagation();

      if (action === 'unsubscribe') {
        this.writeSellingPlan(null, this.dataset.stringUnsubscribed);
        return;
      }

      const rawPlanId = control.dataset.sellingPlanId;
      const planId = Number(rawPlanId);
      if (!rawPlanId || !Number.isInteger(planId) || planId <= 0) return;
      this.writeSellingPlan(planId, this.dataset.stringSubscribed);
    }

    onChange(event) {
      const select = event.target.closest('[data-cart-control="selling-plan"]');
      if (!select || !this.contains(select)) return;

      event.stopPropagation();

      const rawPlanId = select.value;
      const planId = Number(rawPlanId);
      if (!rawPlanId || !Number.isInteger(planId) || planId <= 0) return;
      if (String(planId) === String(select.dataset.currentPlan)) return;

      this.writeSellingPlan(planId, this.dataset.stringSubscribed);
    }
  }

  HbElements.define('hb-cart-line-plan', HbCartLinePlan);

  class HbCartDiscount extends HbElements.Component {
    onConnect() {
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
    }

    onDisconnect() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
    }

    appliedCodes() {
      return Array.prototype.map.call(this.querySelectorAll('[data-discount-remove]'), function (button) {
        return button.getAttribute('data-discount-remove');
      });
    }

    applyFromInput() {
      const input = this.querySelector('[data-discount-input]');
      const code = input && input.value.trim();
      if (!code) return;

      const codes = [];
      this.appliedCodes().forEach(function (applied) {
        if (applied && codes.indexOf(applied) === -1) codes.push(applied);
      });
      const exists = codes.some(function (applied) {
        return applied.toLowerCase() === code.toLowerCase();
      });
      if (!exists) codes.push(code);

      CartController.setDiscounts(codes);
    }

    onClick(event) {
      if (event.target.closest('[data-discount-apply]')) {
        event.preventDefault();
        this.applyFromInput();
        return;
      }

      const remove = event.target.closest('[data-discount-remove]');
      if (remove) {
        event.preventDefault();
        const gone = (remove.getAttribute('data-discount-remove') || '').toLowerCase();
        CartController.setDiscounts(
          this.appliedCodes().filter(function (applied) {
            return applied.toLowerCase() !== gone;
          })
        );
        return;
      }

      if (event.target.closest('[data-discount-clear]')) {
        event.preventDefault();
        CartController.setDiscounts(this.appliedCodes());
      }
    }

    onKeydown(event) {
      if (event.key !== 'Enter') return;
      if (!event.target.closest('[data-discount-input]')) return;
      event.preventDefault();
      this.applyFromInput();
    }
  }

  HbElements.define('hb-cart-discount', HbCartDiscount);

  subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
    if (!event || event.source === 'hb-cart') return;
    if (!document.querySelector(REGION_SELECTOR)) return;
    return CartController.refreshFromServer();
  });
})();
